/**
 * @kia/shared — مودیان / electronic tax invoice (صورتحساب الکترونیکی) contracts. See MOADIAN_SPEC.md.
 *
 * A PROVIDER-AGNOSTIC internal model. The gateway (TSP معتمد / direct / stub) maps this to the tax
 * system's cryptic field codes (taxid/inty/tins/…). Integers are rials; VAT is computed, never trusted
 * from input. A submitted invoice is immutable — corrections are new اصلاحی/ابطالی invoices.
 */
import { z } from 'zod';
import { isValidEconomicCode, isValidNationalOrLegalId } from './iranId.js';
import { MAX_RIAL } from './money.js';
/** الگو — invoice pattern. Start with the sale pattern; correction/void come later. */
export const INVOICE_PATTERNS = ['sale'];
/** نوع — main (اصلی) / correction (اصلاحی) / void (ابطالی). */
export const INVOICE_KINDS = ['main', 'correction', 'void'];
/** Settlement subject (ins): cash (نقدی) / credit (نسیه) / mixed. */
export const SETTLEMENT_TYPES = ['cash', 'credit', 'mixed'];
/**
 * Lifecycle. draft → (compute+sign) → submitted → accepted | rejected. `cancelled` marks one voided by
 * a later ابطالی. Only draft is editable; everything else is immutable.
 */
export const INVOICE_STATUSES = [
    'draft',
    'submitted',
    'accepted',
    'rejected',
    'cancelled',
];
export const INVOICE_STATUS_LABELS_FA = {
    draft: 'پیش‌نویس',
    submitted: 'ارسال‌شده',
    accepted: 'تأییدشده',
    rejected: 'ردشده',
    cancelled: 'ابطال‌شده',
};
/** The default Iranian VAT rate (نرخ مالیات بر ارزش افزوده) — 10% at time of writing; per-line overridable. */
export const DEFAULT_VAT_RATE = 0.1;
// ── Input (draft) ────────────────────────────────────────────────────────────
export const TaxInvoiceLineInputSchema = z
    .object({
    productCode: z.string().trim().min(1, 'کد کالا لازم است').max(40), // maps to sstid (13-digit tax code)
    description: z.string().trim().max(240).default(''),
    // .finite()+.max: an Infinity qty (JSON 1e309) or a >2^53 price/discount overflows the stored net/vat/total
    // and bricks the tax_invoice table on read (money.ts). Cap every money input at the write.
    qty: z.number().positive('مقدار باید مثبت باشد').finite().max(MAX_RIAL),
    unitCode: z.string().trim().max(10).default('1627'), // mu — measurement-unit code
    unitPrice: z.number().int().nonnegative().max(MAX_RIAL, 'مبلغ خارج از محدودهٔ مجاز است'), // fee (rials)
    discount: z
        .number()
        .int()
        .nonnegative()
        .max(MAX_RIAL, 'مبلغ خارج از محدودهٔ مجاز است')
        .default(0), // dis (rials)
    vatRate: z.number().min(0).max(1).default(DEFAULT_VAT_RATE), // vra
})
    // Each field can be in range yet their product overflow; the stored line total must also stay ≤ MAX_RIAL.
    .refine((l) => l.qty * l.unitPrice <= MAX_RIAL, {
    message: 'جمع ردیف خارج از محدودهٔ مجاز است',
    path: ['unitPrice'],
});
export const TaxInvoiceInputSchema = z
    .object({
    dealId: z.number().int().positive().optional(),
    customerId: z.number().int().positive().optional(),
    pattern: z.enum(INVOICE_PATTERNS).default('sale'),
    kind: z.enum(INVOICE_KINDS).default('main'),
    settlement: z.enum(SETTLEMENT_TYPES).default('credit'),
    issuedAt: z.string().trim().min(1), // ISO
    // Buyer (required for a with-buyer sale invoice; economic code / national id per entity type).
    buyerName: z.string().trim().max(200).default(''),
    buyerEconomicCode: z.string().trim().max(20).default(''),
    buyerNationalId: z.string().trim().max(20).default(''),
    lines: z.array(TaxInvoiceLineInputSchema).min(1, 'حداقل یک ردیف لازم است'),
})
    .superRefine((v, ctx) => {
    // THE enforcement point for tax-id validity: مودیان rejects a malformed buyer code, so validate here (when
    // provided) with the shared iranId authority — the same one built for the company profile (E0-04) and
    // deliberately deferred from the free-form CRM (E7-08). A retail (no-buyer) invoice leaves these empty.
    if (v.buyerEconomicCode && !isValidEconomicCode(v.buyerEconomicCode))
        ctx.addIssue({
            code: 'custom',
            path: ['buyerEconomicCode'],
            message: 'شمارهٔ اقتصادی خریدار نامعتبر است (۱۱ یا ۱۲ رقم)',
        });
    if (v.buyerNationalId && !isValidNationalOrLegalId(v.buyerNationalId))
        ctx.addIssue({
            code: 'custom',
            path: ['buyerNationalId'],
            message: 'کد/شناسهٔ ملی خریدار نامعتبر است',
        });
});
//# sourceMappingURL=moadian.js.map